/*
 * Copyright 2017-2017 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License"). You may not use this file except in compliance with
 * the License. A copy of the License is located at
 *
 *     http://aws.amazon.com/apache2.0/
 *
 * or in the "license" file accompanying this file. This file is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
 * CONDITIONS OF ANY KIND, either express or implied. See the License for the specific language governing permissions
 * and limitations under the License.
 */

import * as React from 'react';
import { ConsoleLogger as Logger, Hub, I18n, JS } from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import { AuthPiece } from 'aws-amplify-react';
import { IconButton, PageLoader } from '@kambria/kambria-lego';
// You can use provided image shipped by this package or using your own
import { LinkedIn } from 'react-linkedin-login-oauth2';
import linkedin from 'react-linkedin-login-oauth2/assets/linkedin.png';

import { updateEmailToSendy } from '../../redux/actions/user.action';
import {
  Button,
  FormField,
  FormSection,
  Hint,
  Input,
  InputLabel,
  Link,
  SectionBody,
  SectionFooter,
  SectionFooterPrimaryContent,
  SectionFooterSecondaryContent,
  SectionHeader
} from 'aws-amplify-react/lib/Amplify-UI/Amplify-UI-Components-React';
import validate from './Validate';
import client from '../../helpers/client';
import paths from '../../redux/paths';
import cognito from '../../helpers/cognito';
import { connect } from 'react-redux';

const logger = new Logger('SignIn');
class SignIn extends AuthPiece {
  constructor(props) {
    super(props);

    this.checkContact = this.checkContact.bind(this);
    this.signIn = this.signIn.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this._validAuthStates = ['signIn', 'signedOut', 'signedUp'];
    this.state = {
      loading: false,
    };

    // Store path to return back if using facebook sign in
    if (window.location.pathname !== '/auth') {
      localStorage.setItem('pathBeforeSignIn', `${window.location.pathname}${window.location.search}`);
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown);
  }

  onKeyDown(e) {
    if (this.props.authState === 'signIn') {
      if (e.keyCode === 13) {
        // when press enter
        this.signIn();
      }
    }
  }

  checkContact(user) {
    if (!Auth || typeof Auth.verifiedContact !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    Auth.verifiedContact(user).then((data) => {
      if (!JS.isEmpty(data.verified)) {
        this.changeState('signedIn', user);
        let email = user.attributes.email
        let name = user.attributes.username
        // call api để add email vào sendy
        this.props.updateEmailToSendy(email)
      } else {
        user = Object.assign(user, data);
        this.changeState('verifyContact', user);
      }
    });
  }

  hideLoading = () => {
    this.setState({ loading: false });
  };

  signIn() {
    this.setState({ loading: true });

    let { username = '', password = '' } = this.inputs;
    username = username.replace(/\s/g, '').toLowerCase();
    let { error } = validate.validateInput({ username, password });
    if (error) {
      this.hideLoading();
      return this.error(new Error(error));
    }

    if (!Auth || typeof Auth.signIn !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }

    Auth.signIn(username, password)
      .then((user) => {
        this.hideLoading();

        if (user) {
          if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
            logger.debug('confirm user with ' + user.challengeName);
            this.changeState('confirmSignIn', user);
          } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
            logger.debug('require new password', user.challengeParam);
            this.changeState('requireNewPassword', user);
          } else if (user.challengeName === 'MFA_SETUP') {
            logger.debug('TOTP setup', user.challengeParam);
            this.changeState('TOTPSetup', user);
          } else {
            this.checkContact(user);
          }
        } else {
          this.error(new Error('Get user information failed'));
        }
      })
      .catch((err) => {
        this.hideLoading();
        switch (err.code) {
          case 'UserNotConfirmedException':
            logger.debug('the user is not confirmed');
            this.changeState('confirmSignUp', username);
            break;
          case 'PasswordResetRequiredException':
            logger.debug('the user requires a new password');
            this.changeState('forgotPassword', { username: 'username' });
            break;

          default:
            this.error(err);
            break;
        }
      });
  }

  onSignInFacebookClick = () => {
    try {
      const scope = 'public_profile,email';

      this.setState({ loading: true });

      this.loginFacebook(scope);
    } catch (err) {
      this.hideLoading();

      this.error(new Error(err.message));
    }
  };

  loginFacebook = (scope) => {
    if (!window.FB && navigator.doNotTrack === '1') {
      const message =
        'The Content Blocking settings on this browser are preventing you from using the “Login with Facebook” feature. Please change the settings or use the email login!';
      this.error(new Error(message));
      return this.hideLoading();
    }

    window.FB.login(
      (response) => {
        if (!response || !response.authResponse) {
          return this.hideLoading();
        }
        this.federatedSignIn(response.authResponse);
      },
      {
        scope: scope,
        auth_type: 'rerequest',
      },
    );
  };

  federatedSignIn = (response) => {
    const { accessToken } = response;

    if (!accessToken) {
      return this.hideLoading();
    }

    window.FB.api('/me', { fields: 'email' }, (response) => {
      if (response.email) {
        const user = {
          accessToken: accessToken,
          provider: 'Facebook',
        };

        Hub.dispatch('auth', {
          event: 'federatedSignIn',
          data: user,
        });
      } else {
        this.error(new Error('Can not access email address'));
        Hub.dispatch('auth', {
          event: 'federatedSignInFailure',
          data: {
            message: 'Can not access email address',
          },
        });
        return this.hideLoading();
      }
    });
  };

  signInGoogle = () => {
    const ga = window.gapi.auth2.getAuthInstance();

    ga.signIn().then(
      async (googleUser) => {
        const profile = googleUser.getBasicProfile(),
          user = {
            userId: profile.getId(),
            email: profile.getEmail(),
            name: profile.getName(),
            picture: profile.getImageUrl(),
            accessToken: googleUser.getAuthResponse().id_token,
            provider: 'Google',
          };
        console.log("user", user)
        Hub.dispatch('auth', {
          event: 'federatedSignIn',
          data: user,
        });
      },
      (error) => {
        this.error(new Error(error.error));
        Hub.dispatch('auth', {
          event: 'federatedSignInFailure',
          data: {
            message: error,
          },
        });
      },
    );
  };

  signInLinkedIn = () => {
    let url = 'https://' + window.cognito.AMAZON_COGNITO_DOMAIN + '/login?response_type=token&client_id=' + window.cognito.CLIENT_ID + '&redirect_uri='+ window.location.origin + '/linkedin-callback&identity_provider=LinkedIn'
   console.log("url", url)
    window.location.href = url
  }

  responseMessage = (response) => {
    console.log(response);
  };

  errorMessage = (error) => {
    console.log(error);
  };

  handleSuccessLinkedIn = (code) => {
    let clientId = '86070lq0d8b2s9'
    let secret = 'qit6rEdZnCW1TwRO'
    let user = {
      code: code,
      accessToken: code,
      provider: 'LinkedIn',
    };
    Hub.dispatch('auth', {
      event: 'federatedSignIn',
      data: user,
    });
    // return request.post('https://www.linkedin.com/oauth/v2/accessToken')
    //   .send('grant_type=authorization_code')
    //   .send(`redirect_uri=${window.location.origin}/linkedin-callback`)
    //   .send(`client_id=${clientId}`)
    //   .send(`client_secret=${secret}`)
    //   .send(`code=${code}`).then(rs => {
    //       console.log("rs", rs)
    //   })
  }

  showComponent(theme) {
    const { hide = [] } = this.props;
    if (hide && hide.includes(SignIn)) {
      return null;
    }

    return (
      <FormSection theme={theme}>
        <SectionHeader theme={theme}>{I18n.get('Sign in to proceed')}</SectionHeader>
        <SectionBody theme={theme}>
          <div className="mb-4">
             <IconButton
              type="gg"
              size="sm"
              className="mr-4"
              customStyle={{ width: '100%' }}
              onClick={this.signInGoogle}
            >
              Continue with Google
            </IconButton>
          </div>
          <div className="mb-4">
            <button style={{width: '100%'}} className="btn btn-linkedIn btn-sm mr-4" onClick={this.signInLinkedIn}>Continue with Linkedin</button>
            {/*<LinkedIn*/}
            {/*  clientId="86070lq0d8b2s9"*/}
            {/*  redirectUri={`${window.location.origin}/linkedin-callback`}*/}
            {/*  scope="profile email openid"*/}
            {/*  onSuccess={(code) => {*/}
            {/*    this.handleSuccessLinkedIn(code)*/}
            {/*  }}*/}
            {/*  onError={(error) => {*/}
            {/*    console.log('error', error);*/}
            {/*  }}*/}
            {/*>*/}
            {/*  {({ linkedInLogin }) => (*/}
            {/*    <img*/}
            {/*      onClick={linkedInLogin}*/}
            {/*      src={linkedin}*/}
            {/*      alt="Sign in with Linked In"*/}
            {/*      style={{ maxWidth: '180px', cursor: 'pointer' }}*/}
            {/*    />*/}
            {/*  )}*/}
            {/*</LinkedIn>*/}
          </div>
          <p>
            <span
              style={{
                background: '#fff',
                padding: '0 10px',
                color: '#8a8a8a',
              }}
            >
              {/* or */}
            </span>
          </p>
          <FormField theme={theme}>
            <InputLabel>{I18n.get('Email')} *</InputLabel>
            <Input
              autoFocus
              placeholder={I18n.get('Enter your email')}
              theme={theme}
              key="username"
              name="username"
              onChange={this.handleInputChange}
            />
          </FormField>
          <FormField theme={theme}>
            <InputLabel>{I18n.get('Password')} *</InputLabel>
            <Input
              placeholder={I18n.get('Enter your password')}
              theme={theme}
              key="password"
              type="password"
              name="password"
              onChange={this.handleInputChange}
            />
            <Hint theme={theme}>
              {I18n.get('Forgot your password? ')}
              <Link color="primary" theme={theme} onClick={() => this.changeState('forgotPassword')}>
                {I18n.get('Reset password')}
              </Link>
            </Hint>
          </FormField>
        </SectionBody>
        <SectionFooter theme={theme}>
          <SectionFooterPrimaryContent theme={theme}>
            <Button theme={theme} onClick={this.signIn}>
              {I18n.get('Sign In')}
            </Button>
          </SectionFooterPrimaryContent>
          {
            <SectionFooterSecondaryContent theme={theme}>
              {I18n.get('No account? ')}
              <Link color="primary" theme={theme} onClick={() => this.changeState('signUp')}>
                {I18n.get('Create account')}
              </Link>
            </SectionFooterSecondaryContent>
          }
        </SectionFooter>
        {this.state.loading && (
          <div
            style={{
              position: 'fixed',
              width: '100%',
              height: '100%',
              top: 0,
              background: '#0000004d',
              left: 0,
            }}
          >
            <PageLoader type="center" />
          </div>
        )}
      </FormSection>
    );
  }
}

export default connect(null, { updateEmailToSendy })(SignIn);
